import React,{useState} from 'react';
import {motion} from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import {useAuth} from '../../context/AuthContext';
import {profileService} from '../../services/profileService';
import BeltPicker from '../BeltPicker';
import Register from '../auth/Register';
import BeltIcon from '../BeltIcon';
import {beltLabel} from '../../data/belts';
import {useDojangs} from '../../hooks/useDojangs';

const {FiSave,FiLock,FiCheck}=FiIcons;

const MIN_PASSWORD_LENGTH=6;

function StudentProfile() {
  const {user,setUser}=useAuth();
  const {dojangs,reload: reloadDojangs}=useDojangs();
  const [profile,setProfile]=useState({name: user.name,phone: user.phone,birthDate: user.birthDate,tkdStartDate: user.tkdStartDate,belt: user.belt,dojang: user.dojang,dojangOther: user.dojangOther || ''});
  const [passwords,setPasswords]=useState({current: '',next: '',confirm: ''});
  const [status,setStatus]=useState({profile: null,password: null});
  const [saving,setSaving]=useState({profile: false,password: false});

  const updateProfile=(field)=> (e)=> setProfile({...profile,[field]: e.target.value});
  const updatePassword=(field)=> (e)=> setPasswords({...passwords,[field]: e.target.value});

  const saveProfile=async (e)=> {
    e.preventDefault();
    setSaving(s=> ({...s,profile: true}));
    try {
      const updated=await profileService.update(profile);
      setUser(updated);
      setProfile(current=> ({...current,dojang: updated.dojang,dojangOther: ''}));
      reloadDojangs();
      setStatus(s=> ({...s,profile: {ok: true,message: 'Perfil actualizado'}}));
    } catch (error) {
      setStatus(s=> ({...s,profile: {ok: false,message: error.message}}));
    } finally {
      setSaving(s=> ({...s,profile: false}));
    }
  };

  const savePassword=async (e)=> {
    e.preventDefault();
    if (passwords.next!==passwords.confirm) {
      setStatus(s=> ({...s,password: {ok: false,message: 'Las contraseñas no coinciden'}}));
      return;
    }
    setSaving(s=> ({...s,password: true}));
    try {
      await profileService.changePassword(passwords.current,passwords.next);
      setPasswords({current: '',next: '',confirm: ''});
      setStatus(s=> ({...s,password: {ok: true,message: 'Contraseña actualizada'}}));
    } catch (error) {
      setStatus(s=> ({...s,password: {ok: false,message: error.message}}));
    } finally {
      setSaving(s=> ({...s,password: false}));
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-6 pb-20">
      <div className="mb-12">
        <span className="text-red-500 font-black tracking-[0.4em] uppercase text-[10px] mb-4 block">Mi cuenta</span>
        <h1 className="text-5xl font-black uppercase tracking-tighter text-white [text-shadow:_0_2px_10px_rgba(0,0,0,0.5)]">
          Mi perfil
        </h1>
        <div className="mt-4 flex items-center gap-3 text-white/80 text-xs font-bold uppercase tracking-widest">
          <BeltIcon type={user.belt} className="w-12 h-4" />
          {beltLabel(user.belt)} · {user.email}
        </div>
      </div>

      <div className="space-y-8">
        <motion.form initial={{opacity: 0,y: 20}} animate={{opacity: 1,y: 0}} onSubmit={saveProfile} className="glass-card rounded-[2.5rem] p-8 md:p-10 space-y-8">
          <div className="grid md:grid-cols-2 gap-6">
            <Field label="Nombre completo *">
              <input required maxLength={120} value={profile.name} onChange={updateProfile('name')} className="profile-input" />
            </Field>
            <Field label="Dojang *">
              <select required value={profile.dojang} onChange={updateProfile('dojang')} className="profile-input [color-scheme:dark]">
                {dojangs.map(d=> <option key={d.id} value={d.id}>{d.label}</option>)}
              </select>
            </Field>
            {profile.dojang==='otro' && (
              <Field label="Nombre de tu dojang *">
                <input required maxLength={80} value={profile.dojangOther} onChange={updateProfile('dojangOther')} className="profile-input" />
              </Field>
            )}
            <Field label="Teléfono">
              <input type="tel" maxLength={30} value={profile.phone} onChange={updateProfile('phone')} className="profile-input" />
            </Field>
            <Field label="Fecha de nacimiento">
              <input type="date" min="1900-01-01" max={new Date().toLocaleDateString('en-CA')} value={profile.birthDate} onChange={updateProfile('birthDate')} className="profile-input [color-scheme:dark]" />
            </Field>
            <Field label="Fecha de ingreso al TKD">
              <input type="date" min={profile.birthDate || '1900-01-01'} max={new Date().toLocaleDateString('en-CA')} value={profile.tkdStartDate} onChange={updateProfile('tkdStartDate')} className="profile-input [color-scheme:dark]" />
            </Field>
          </div>

          <Field label="Grado actual">
            <p className="text-white/70 text-xs mb-4 ml-2">
              El módulo de Entrenamiento te sugiere practicar hasta este grado.
            </p>
            <BeltPicker value={profile.belt} onChange={belt=> setProfile({...profile,belt})} markedBelt={user.belt} />
          </Field>

          <FormFooter status={status.profile}>
            <button type="submit" disabled={saving.profile} className="bg-red-600 hover:bg-red-500 disabled:opacity-60 text-white px-8 py-4 rounded-2xl font-black uppercase tracking-widest text-[10px] flex items-center gap-2">
              <SafeIcon icon={FiSave} /> {saving.profile ? 'Guardando...' : 'Guardar cambios'}
            </button>
          </FormFooter>
        </motion.form>

        <motion.form initial={{opacity: 0,y: 20}} animate={{opacity: 1,y: 0}} onSubmit={savePassword} className="glass-card rounded-[2.5rem] p-8 md:p-10 space-y-8">
          <h2 className="text-white font-black uppercase tracking-widest text-sm flex items-center gap-2">
            <SafeIcon icon={FiLock} className="text-red-500" /> Cambiar contraseña
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <Field label="Contraseña actual">
              <input type="password" required autoComplete="current-password" value={passwords.current} onChange={updatePassword('current')} className="profile-input" />
            </Field>
            <Field label="Nueva contraseña">
              <input type="password" required minLength={MIN_PASSWORD_LENGTH} autoComplete="new-password" value={passwords.next} onChange={updatePassword('next')} className="profile-input" placeholder={`Mínimo ${MIN_PASSWORD_LENGTH} caracteres`} />
            </Field>
            <Field label="Confirmar nueva">
              <input type="password" required minLength={MIN_PASSWORD_LENGTH} autoComplete="new-password" value={passwords.confirm} onChange={updatePassword('confirm')} className="profile-input" />
            </Field>
          </div>
          <FormFooter status={status.password}>
            <button type="submit" disabled={saving.password} className="bg-white/10 hover:bg-white/20 disabled:opacity-60 text-white px-8 py-4 rounded-2xl font-black uppercase tracking-widest text-[10px] flex items-center gap-2 border border-white/10">
              <SafeIcon icon={FiLock} /> {saving.password ? 'Guardando...' : 'Actualizar contraseña'}
            </button>
          </FormFooter>
        </motion.form>
      </div>
    </div>
  );
}

function Field({label,children}) {
  return (
    <div className="space-y-2">
      <label className="text-[10px] font-black uppercase tracking-widest text-white ml-2">{label}</label>
      {children}
    </div>
  );
}

function FormFooter({status,children}) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-end gap-4">
      {status && (
        <span className={`text-xs font-bold uppercase tracking-wider flex items-center gap-2 ${status.ok ? 'text-green-400' : 'text-red-400'}`}>
          {status.ok && <SafeIcon icon={FiCheck} />} {status.message}
        </span>
      )}
      {children}
    </div>
  );
}


// Los invitados no tienen perfil: se les invita a registrarse como alumnos.
function GuestProfile() {
  const [registering,setRegistering]=useState(false);

  if (registering) return <Register onBack={()=> setRegistering(false)} />;

  return (
    <div className="max-w-xl mx-auto px-6 pb-24">
      <div className="glass-card rounded-[2.5rem] p-8 md:p-12 text-center">
        <h2 className="text-3xl font-black uppercase tracking-tighter text-white [text-shadow:_0_2px_8px_rgba(0,0,0,0.85)]">
          Estás como invitado
        </h2>
        <p className="text-white mt-5 leading-relaxed [text-shadow:_0_1px_6px_rgba(0,0,0,0.9)]">
          Los invitados pueden consultar eventos y videos, pero no tienen perfil.
          Si eres alumno del dojang, crea tu cuenta para guardar tu grado y que tu profesor siga tu entrenamiento.
        </p>
        <button
          onClick={()=> setRegistering(true)}
          className="mt-8 w-full bg-white text-zinc-900 hover:bg-zinc-200 py-4 rounded-2xl font-black uppercase tracking-[0.3em] text-[11px] transition-all shadow-xl active:scale-95"
        >
          Registrarme como alumno
        </button>
      </div>
    </div>
  );
}

export default function ProfileView() {
  const {isGuest}=useAuth();
  return isGuest ? <GuestProfile /> : <StudentProfile />;
}
